import { sha256 } from "js-sha256";
import { v4 as uuid } from "uuid";

import { Currency } from "@/Apollo/schema";
import { authStore } from "@/auth";
import {
  AnalyticsMe,
  AnalyticsOrderGroup,
  AnalyticsProduct,
  AnalyticsProvider,
} from "@/common/contracts/analytics.contracts";
import { LISTING_CURRENCY } from "@/common/static/currency.static";
import { getPageType } from "@/common/utils/analytics.utils";
import { isNil } from "@/common/utils/value.utils";

/**
 * Pinterest provider — the Pinterest Tag events the Verified Merchant Program scores on
 * (WHO-5690).
 *
 * The tag itself (`pintrk`) is loaded by GTM, so this provider never calls Pinterest directly. It
 * pushes ONE data layer event, `pinterest_event`, carrying the Pinterest event name and a payload
 * already in Pinterest's own schema. A single GTM Custom HTML tag relays it verbatim:
 *
 *   pintrk('track', {{DLV - pinterest_event_name}}, {{DLV - pinterest_event_data}});
 *
 * Why not map the GA4 `view_item` / `purchase` events inside GTM instead: Pinterest wants
 * `line_items[].product_id` etc., not `items[].item_id`, and a mapping written in a GTM variable is
 * invisible to code review and untested. Here the shape is typed and unit-tested.
 *
 * Identity for enhanced match travels on every push as `pinterest_user` (`em` = SHA-256 of the
 * lower-cased, trimmed email; `external_id` = SHA-256 of the member id). Both are hashed here so
 * nothing personal reaches the data layer in clear. The GTM base tag reads them into
 * `pintrk('load', TAG_ID, {em, external_id})`.
 *
 * Event ids: `pagevisit` gets a fresh uuid; `checkout` is deterministic per order group so a
 * refresh of the success page (which re-fires `purchase` in GA too) dedupes on Pinterest's side
 * and a later Conversions API event can carry the same id.
 */

export const PINTEREST_DATA_LAYER_EVENT = "pinterest_event";

export type PinterestEventName =
  | "pagevisit"
  | "addtocart"
  | "checkout"
  | "signup";

export type PinterestLineItem = {
  product_id: string;
  product_name?: string;
  product_price?: number;
  product_brand?: string;
  product_category?: string;
  product_quantity: number;
};

export type PinterestEventData = {
  event_id: string;
  currency?: Currency;
  value?: number;
  order_id?: string;
  order_quantity?: number;
  line_items?: PinterestLineItem[];
};

export type PinterestUser = {
  em?: string;
  external_id?: string;
};

type PinterestDataLayerPush = {
  event: typeof PINTEREST_DATA_LAYER_EVENT;
  pinterest_event_name: PinterestEventName;
  pinterest_event_data: PinterestEventData;
  pinterest_user: PinterestUser;
};

/** Last known hashed email — `trackPageView` is the only call that sees `me`. */
let hashedEmail: string | undefined;

const hashEmail = (email?: string | null) =>
  email ? sha256(email.trim().toLowerCase()) : undefined;

const currentUser = (): PinterestUser => {
  const userId = authStore.getState().userId;

  return {
    em: hashedEmail,
    external_id: userId ? sha256(userId) : undefined,
  };
};

const pushPinterestEvent = (
  eventName: PinterestEventName,
  eventData: PinterestEventData
) => {
  if (typeof window === "undefined") {
    return;
  }

  window.dataLayer = window.dataLayer ?? [];

  /* GTM merges object pushes into its data layer model, so without this reset a bare pagevisit
   * pushed after a PDP visit would still carry that product's line_items when the relay tag reads
   * the model. Same trick as clearEcommerceFromDataLayer in the GA provider. */
  window.dataLayer.push({ pinterest_event_data: null, pinterest_user: null });

  const push: PinterestDataLayerPush = {
    event: PINTEREST_DATA_LAYER_EVENT,
    pinterest_event_name: eventName,
    pinterest_event_data: eventData,
    pinterest_user: currentUser(),
  };

  window.dataLayer.push(push);
};

/**
 * Pinterest joins events to the catalog on `product_id`, so it must equal the feed's `id` — which
 * is the product uuid (whoppah-feeds `xml_builder.py`). Brand and category use titles because the
 * feed sends `g:brand` = brand title and `g:product_type` = category titles.
 */
export const productToPinterestLineItem = (
  product: AnalyticsProduct
): PinterestLineItem | undefined => {
  if (isNil(product.id)) {
    return undefined;
  }

  /** Same reversal as the GA item: the feed lists the deepest category first. */
  const categories = product.categories?.filter(Boolean).reverse();
  const category = categories
    ?.map(category => category?.title)
    .filter(Boolean)
    .join(" > ");

  return {
    product_id: product.id,
    product_name: product.title ?? undefined,
    product_price: product.auction?.buy_now_price?.amount ?? undefined,
    product_brand: product.brand?.title ?? undefined,
    product_category: category || undefined,
    product_quantity: product.number_of_items ?? 1,
  };
};

const productEventData = (
  product: AnalyticsProduct
): PinterestEventData | undefined => {
  const lineItem = productToPinterestLineItem(product);

  if (!lineItem) {
    return undefined;
  }

  return {
    event_id: uuid(),
    currency: product.auction?.buy_now_price?.currency ?? LISTING_CURRENCY,
    value: lineItem.product_price,
    order_quantity: lineItem.product_quantity,
    line_items: [lineItem],
  };
};

export const pinterestAnalyticsProvider: AnalyticsProvider = {
  trackPageView: (
    _pageUrl: string,
    _pageTitle: string,
    _language: string,
    me?: AnalyticsMe
  ) => {
    hashedEmail = hashEmail(me?.email ?? me?.merchants?.[0]?.email);

    /* The PDP page visit is sent by `trackViewItem`, with the product attached. Sending a bare
     * one here as well would double-count every PDP. */
    if (getPageType() === "pdp") {
      return;
    }

    pushPinterestEvent("pagevisit", { event_id: uuid() });
  },
  trackViewItem: (product: AnalyticsProduct) => {
    const eventData = productEventData(product);

    if (eventData) {
      pushPinterestEvent("pagevisit", eventData);
    }
  },
  trackAddToCart: (product: AnalyticsProduct) => {
    const eventData = productEventData(product);

    if (eventData) {
      pushPinterestEvent("addtocart", eventData);
    }
  },
  trackPlaceBidSuccessPdp: (_bidMethod, product: AnalyticsProduct) => {
    /* A placed bid is the marketplace's mid-funnel intent, the closest thing to a cart. */
    const eventData = productEventData(product);

    if (eventData) {
      pushPinterestEvent("addtocart", eventData);
    }
  },
  trackPurchase: (orderGroup: AnalyticsOrderGroup) => {
    if (isNil(orderGroup.id)) {
      return;
    }

    const lineItems = (orderGroup.orders ?? []).reduce<PinterestLineItem[]>(
      (items, order) => {
        const lineItem = order?.product
          ? productToPinterestLineItem(order.product)
          : undefined;

        if (lineItem) {
          items.push(lineItem);
        }

        return items;
      },
      []
    );

    if (lineItems.length === 0) {
      return;
    }

    pushPinterestEvent("checkout", {
      event_id: `checkout-${orderGroup.id}`,
      order_id: orderGroup.id,
      currency: orderGroup.totals?.subtotal?.currency ?? LISTING_CURRENCY,
      value: orderGroup.totals?.subtotal?.amount ?? undefined,
      order_quantity: lineItems.reduce(
        (sum, lineItem) => sum + lineItem.product_quantity,
        0
      ),
      line_items: lineItems,
    });
  },
  trackSignUp: () => {
    pushPinterestEvent("signup", { event_id: uuid() });
  },
};
